import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link2, DoorClosed, HelpCircle } from "lucide-react";
import { WinButton } from "./WinComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal "Liste des demandes orphelines" — parité `FrmListHomolog.vb`.
 * Affiche les demandes d'homologation d'un bureau dont `noSeq_req` est vide
 * (créées par un corequérant d'un autre bureau) pour permettre à l'utilisateur
 * de les lier manuellement à son dossier courant.
 */
const HomologOrphelinesModal = ({ isOpen, onClose, nobur, currentReq, onLier,
                                  showPopup }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selIdx, setSelIdx] = useState(-1);
  const [showAide, setShowAide] = useState(false);

  const load = useCallback(async () => {
    if (!nobur) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/homolog/orphelines`,
                                       { params: { nobur } });
      setRows(Array.isArray(data) ? data : []);
      setSelIdx(-1);
    } catch (e) {
      showPopup?.("Erreur lors du chargement des demandes orphelines.",
                  "error", "Erreur");
    } finally {
      setLoading(false);
    }
  }, [nobur, showPopup]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const doLier = async () => {
    if (selIdx < 0 || !rows[selIdx]) {
      showPopup?.("S.v.p. sélectionner une ligne !", "warning", "Homologation");
      return;
    }
    const line = rows[selIdx];
    if (!window.confirm("Vous allez modifier l'information provenant "
                        + "d'un autre bureau, voulez-vous continuer ?")) return;
    try {
      const { data } = await axios.post(
        `${API}/homolog/${line.row_id}/lier`,
        {
          noReg_req:     currentReq.noReg,
          noBur_req:     currentReq.noBur,
          noSeq_req:     currentReq.noSeq,
          nom_req:       currentReq.nom,
          prenom_req:    currentReq.prenom,
          dateNaiss_req: currentReq.dateNaiss,
          natures_req:   currentReq.natures,
        }
      );
      if (data?.success) {
        showPopup?.(`Ligne liée avec succès (homolog_id: ${data.homolog_id})`,
                    "info", "Homologation");
        onLier?.(data.data);
        onClose?.();
      }
    } catch (e) {
      showPopup?.("Erreur lors de la liaison : "
                  + (e.response?.data?.detail || e.message),
                  "error", "Erreur");
    }
  };

  if (!isOpen) return null;

  const cellCss = { border: "1px solid #b0b0b0", padding: "3px 6px", fontSize: 11 };
  const cellRO  = { ...cellCss, background: "#e8e8e8" };
  const cellHi  = { ...cellCss, background: "#d0d0d0" };

  return (
    <div className="popup-overlay" data-testid="homolog-orphelines-modal"
         style={{ zIndex: 10001 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#dbe6f0", border: "1px solid #808080",
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
        width: "min(1220px, 98vw)", maxHeight: "94vh",
        display: "flex", flexDirection: "column",
      }}>
        {/* Title bar style Windows */}
        <div style={{
          background: "linear-gradient(to bottom, #4a7ba8, #234f78)",
          padding: "5px 10px", fontSize: 13, fontWeight: 600, color: "#fff",
          borderBottom: "1px solid #808080",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span data-testid="orphelines-titre">Liste des demandes orphelines</span>
          <button onClick={onClose}
                  style={{ background: "transparent", border: "none", color: "#fff",
                           cursor: "pointer", fontSize: 16, fontWeight: 700,
                           padding: "0 4px" }}>×</button>
        </div>

        {/* Toolbar Windows */}
        <div className="win-toolbar" style={{ padding: "6px 10px", gap: 6 }}>
          <WinButton IconComponent={HelpCircle}
                     onClick={() => setShowAide(a => !a)}
                     data-testid="btn-aide-orphelines"
                     title="Afficher/masquer l'aide">
            {showAide ? "Fermer l'aide" : "Aide?"}
          </WinButton>
          <WinButton IconComponent={Link2} onClick={doLier}
                     disabled={selIdx < 0}
                     data-testid="btn-lier-orpheline"
                     title="Lier la ligne sélectionnée">Lier</WinButton>
          <WinButton IconComponent={DoorClosed} onClick={onClose}
                     data-testid="btn-fermer-orphelines"
                     title="Fermer">Fermer</WinButton>
        </div>

        <div style={{ padding: 8, overflow: "auto", flex: 1 }}>
          {showAide && (
            <div data-testid="aide-orphelines-text"
                 style={{ background: "#fef9e7", border: "1px solid #f39c12",
                          padding: 8, marginBottom: 8, fontSize: 11,
                          lineHeight: 1.4 }}>
              {"Cette liste contient les demandes d'homologation, fournies par les "
              + "ex-conjoints, qui n'ont pas encore de numéro de séquence. Elle "
              + "permet de lier manuellement l'information qui n'a pas été liée "
              + "automatiquement, souvent à cause d'erreur de saisie (ex.: "
              + "orthographe différente)."}<br /><br />
              {"Les informations du "}<b>requérant</b>{" correspondent à celles "
              + "de votre requérant et celles du "}<b>corequérant</b>{" à celles "
              + "de l'ex-conjoint."}<br /><br />
              {"Si vous liez une ligne sélectionnée, l'information concernant le "
              + "requérant de cette ligne sera mise à jour par vos informations, "
              + "et l'information du corequérant sera affichée dans les cases "
              + "appropriées du corequérant."}<br /><br />
              <b>⚠ Attention</b>{" de ne pas lier les informations qui ne "
              + "concernent pas votre requérant."}
            </div>
          )}

          {/* Grid des orphelines */}
          <div style={{ border: "1px solid #808080", background: "#fff",
                        maxHeight: 460, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 11 }}>
              <thead style={{ background: "linear-gradient(to bottom, #dbe6f0, #b8ccd8)",
                              position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ ...cellCss, fontWeight: 700, textAlign: "left" }}>Région co-req.</th>
                  <th style={{ ...cellCss, fontWeight: 700, textAlign: "left" }}>Bureau co-req.</th>
                  <th style={{ ...cellCss, fontWeight: 700, textAlign: "left" }}>Nom du co-req.</th>
                  <th style={{ ...cellCss, fontWeight: 700, textAlign: "left" }}>Prénom du co-req.</th>
                  <th style={{ ...cellCss, fontWeight: 700, textAlign: "left" }}>Nature co-req.</th>
                  <th style={{ ...cellCss, fontWeight: 700, textAlign: "left" }}>Date naiss.</th>
                  <th style={{ ...cellCss, fontWeight: 700, textAlign: "left" }}>No. Séq. co-req.</th>
                  <th style={{ ...cellCss, fontWeight: 700, textAlign: "center" }}>Form. req.</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 12,
                        color: "#666" }}>Chargement…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 12,
                      color: "#999", fontStyle: "italic" }}>
                    Aucune demande orpheline pour ce bureau
                  </td></tr>
                )}
                {rows.map((r, i) => {
                  const sel = i === selIdx;
                  return (
                    <tr key={r.row_id}
                        data-testid={`orpheline-row-${i}`}
                        onClick={() => setSelIdx(i)}
                        onDoubleClick={doLier}
                        style={{ background: sel ? "#a8d8ea" : "transparent",
                                 cursor: "pointer" }}>
                      <td style={sel ? cellHi : cellRO}>{r.noReg_req2 || ""}</td>
                      <td style={sel ? cellHi : cellRO}>{r.noBur_req2 || ""}</td>
                      <td style={sel ? cellHi : cellCss}><b>{r.nom_req2 || ""}</b></td>
                      <td style={sel ? cellHi : cellCss}><b>{r.prenom_req2 || ""}</b></td>
                      <td style={sel ? cellHi : cellCss}>{r.natures_req2 || "-"}</td>
                      <td style={sel ? cellHi : cellCss}>{r.dateNaiss_req2 || ""}</td>
                      <td style={sel ? cellHi : cellCss}>{r.noSeq_req2 || ""}</td>
                      <td style={{ ...(sel ? cellHi : cellCss), textAlign: "center" }}>
                        {r.formRequis_req2 === "1" ? "✓" : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Statut : nombre de lignes + sélection */}
          <div style={{ marginTop: 6, fontSize: 11, color: "#333",
                        background: "#eaf1f8", border: "1px solid #b8ccd8",
                        padding: "4px 8px", display: "flex", justifyContent: "space-between" }}>
            <span data-testid="orphelines-count">
              {loading ? "…" : `${rows.length} demande(s) orpheline(s)`}
            </span>
            <span data-testid="orphelines-selection">
              {selIdx >= 0 && rows[selIdx]
                ? `Sélection : ${rows[selIdx].nom_req2 || ""}, ${rows[selIdx].prenom_req2 || ""}`
                : "Aucune sélection"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomologOrphelinesModal;
